import { Controller } from "@hotwired/stimulus"

// Every refresh of the /iot dashboard goes through here, so the page asks the
// server for its frame at most once per WINDOW_MS, never drops a refresh, and
// asks nobody while nobody can see it.
//
// What is refreshed is the page's one frame (the turbo-frame this element sits
// in, Iot::LivePages.dashboard_frame_id), never the page: the frame's request
// leaves the navbar, the command palette and the rest of the layout alone.
//
// Two things ask for one:
// - The server, with a Turbo Stream `frame_reload` aimed at that frame
//   (Iot::LiveRenderer). The page listens on one stream per branch it covers
//   plus the unassigned bucket, and the server limits each STREAM to one
//   reload per window — so one change can arrive twice (its branch's and the
//   bucket's), and a page over many branches more. Here they merge per PAGE:
//   the first goes out after MERGE_MS, any arriving within WINDOW_MS of the
//   last waits for the window to close, and one reload then covers them all.
//   A `frame_reload` aimed at any other frame is left to its own action.
// - Its clock: `seconds` is when the next value that changes with time alone
//   is due (Iot::DashboardsController#clock_refresh_in — midnight, when
//   yesterday leaves the days the Warnings and Alerts tiles count). It comes
//   with every load of the frame, and is re-armed after every one, pushed or
//   timed.
//
// The frame arrives `disabled`, already holding what its `src` would load
// (see the view for why `complete` alone cannot say so): connecting marks it
// complete and only then enables it, so the first paint is its first load.
//
// The reload is the frame's own (`reload()`, what a `frame_reload` runs): the
// frame's `refresh="morph"` morphs it in place, so the window keeps its scroll
// and this controller — inside the frame — keeps its state. A second reload
// would cancel the first one's request, so one asked for while ours is in
// flight waits for it to load (up to LOAD_TIMEOUT_MS, in case it never does)
// rather than restart it, and then for the window. A hidden tab holds its
// reload until shown.
const MERGE_MS = 500
const WINDOW_MS = 5000
const LOAD_TIMEOUT_MS = 10000

export default class extends Controller {
  static values = { seconds: Number }

  connect() {
    this.frame = this.element.closest("turbo-frame")
    if (this.frame?.hasAttribute("disabled")) {
      this.frame.setAttribute("complete", "")
      this.frame.removeAttribute("disabled")
    }
    this.refreshedAt = 0
    this.onStreamRender = this.#intercept.bind(this)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
    this.schedule()
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-stream-render",
      this.onStreamRender,
    )
    clearTimeout(this.clock)
    this.#cancelPending()
  }

  // turbo:frame-load — the frame was just rendered. Any other frame on the
  // page (the command palette's, say) loading is no news here.
  schedule(event) {
    if (event && event.target !== this.frame) return

    this.refreshedAt = Date.now()
    this.loadingSince = null
    clearTimeout(this.clock)
    // A reload asked for while ours was loading: the frame just rendered may
    // predate what it was asked for, so it still goes out, once the window
    // from this render closes, like any other.
    if (this.pending) {
      this.#cancelPending()
      this.request()
    }
    if (this.secondsValue > 0) {
      this.clock = setTimeout(() => this.request(), this.secondsValue * 1000)
    }
  }

  request() {
    if (this.pending) return

    const wait = Math.max(MERGE_MS, this.refreshedAt + WINDOW_MS - Date.now())
    this.pending = setTimeout(() => this.#refresh(), wait)
  }

  // A `frame_reload` aimed at this page's frame, whatever stream sent it: a
  // stream added later reloads the same frame, so it goes through the gate
  // too (merged, never dropped). Any other frame's reload, and any other
  // action, is Turbo's own.
  #intercept(event) {
    const stream = event.target
    if (stream?.getAttribute?.("action") !== "frame_reload") return
    if (!this.frame || stream.getAttribute("target") !== this.frame.id) return

    event.detail.render = () => this.request()
  }

  #refresh() {
    if (document.hidden) {
      this.onVisible = () => {
        if (document.hidden) return
        document.removeEventListener("visibilitychange", this.onVisible)
        this.onVisible = null
        this.#refresh()
      }
      document.addEventListener("visibilitychange", this.onVisible)
      return
    }

    if (this.loadingSince && Date.now() - this.loadingSince < LOAD_TIMEOUT_MS) {
      this.pending = setTimeout(() => this.#refresh(), MERGE_MS)
      return
    }

    this.pending = null
    this.refreshedAt = Date.now()
    this.loadingSince = Date.now()
    this.frame?.reload()
  }

  #cancelPending() {
    clearTimeout(this.pending)
    this.pending = null
    if (this.onVisible) {
      document.removeEventListener("visibilitychange", this.onVisible)
      this.onVisible = null
    }
  }
}
